import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Zap, Save, Shield, Sword, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { powerAPI } from '../utils/api';

const POWER_LEVELS = [3, 7, 12, 16, 19, 22, 27, 32];

const CLASS_CONFIG = {
  tank: { name: 'Tank', icon: Shield, color: 'blue', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-700' },
  warrior: { name: 'Atacante', icon: Sword, color: 'red', bgColor: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-700' },
  support: { name: 'Suporte', icon: Heart, color: 'green', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-700' }
};

const PowerSlot = ({ level, classType, power, template, saving, onInputChange, onSave }) => {
  const config = CLASS_CONFIG[classType];
  
  return (
    <div 
      className={`bg-white rounded-xl p-4 border-2 ${template ? 'border-green-200' : 'border-slate-200'}`}
    >
      <div className="flex items-center gap-4 mb-3">
        <div className={`px-3 py-1 rounded-lg ${config.bgColor} ${config.textColor} font-bold text-sm`}>
          Nível {level}
        </div>
        {template && (
          <span className="text-xs text-green-600 font-medium">✓ Configurado</span>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-3">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Nome do Poder</label>
          <Input
            data-testid={`power-name-${classType}-${level}`}
            value={power.name || ''}
            onChange={(e) => onInputChange('name', e.target.value)}
            placeholder="Ex: Investida"
            className="h-10"
          />
        </div>
        
        <div className="md:col-span-5">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Descrição</label>
          <Textarea
            data-testid={`power-description-${classType}-${level}`}
            value={power.description || ''}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Descreva o efeito do poder..."
            className="h-10 min-h-[40px] resize-none"
            rows={1}
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-slate-700 mb-1 block">
            <Zap className="w-3 h-3 inline mr-1" />
            Energia
          </label>
          <Input
            data-testid={`power-energy-${classType}-${level}`}
            type="number"
            value={power.energy_cost || 10}
            onChange={(e) => onInputChange('energy_cost', e.target.value)}
            min={1}
            max={100}
            className="h-10"
          />
        </div>
        
        <div className="md:col-span-2">
          <Button
            data-testid={`save-power-${classType}-${level}`}
            onClick={onSave}
            disabled={saving || !power.name?.trim()}
            className="w-full h-10"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? '...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const PowersGrid = ({ classType, powers, templates, saving, onInputChange, onSave }) => {
  const config = CLASS_CONFIG[classType];
  const Icon = config.icon;
  
  return (
    <Card className={`p-6 ${config.bgColor} border-2 ${config.borderColor}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-12 h-12 rounded-xl ${config.bgColor} border-2 ${config.borderColor} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${config.textColor}`} />
        </div>
        <div>
          <h2 className={`text-xl font-outfit font-bold ${config.textColor}`}>
            Poderes do {config.name}
          </h2>
          <p className="text-sm text-slate-600">8 poderes desbloqueados nos níveis 3, 7, 12, 16, 19, 22, 27 e 32</p>
        </div>
      </div>

      <div className="space-y-4">
        {POWER_LEVELS.map((level) => (
          <PowerSlot
            key={`${classType}-${level}`}
            level={level}
            classType={classType}
            power={powers[level] || { name: '', description: '', energy_cost: 10 }}
            template={templates[level]}
            saving={saving[`${classType}-${level}`]}
            onInputChange={(field, value) => onInputChange(classType, level, field, value)}
            onSave={() => onSave(classType, level)}
          />
        ))}
      </div>
    </Card>
  );
};

const AdminPowers = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({ tank: {}, warrior: {}, support: {} });
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('tank');
  const [tankPowers, setTankPowers] = useState({});
  const [warriorPowers, setWarriorPowers] = useState({});
  const [supportPowers, setSupportPowers] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    loadTemplates();
  }, []);

  const initializePowers = (data) => {
    const tank = {};
    const warrior = {};
    const support = {};
    
    POWER_LEVELS.forEach(level => {
      tank[level] = data.tank?.[level] || { name: '', description: '', energy_cost: 10 };
      warrior[level] = data.warrior?.[level] || { name: '', description: '', energy_cost: 10 };
      support[level] = data.support?.[level] || { name: '', description: '', energy_cost: 10 };
    });
    
    setTankPowers(tank);
    setWarriorPowers(warrior);
    setSupportPowers(support);
  };

  const loadTemplates = async () => {
    try {
      const response = await powerAPI.getTemplates();
      setTemplates(response.data);
      initializePowers(response.data);
    } catch (error) {
      console.error('Failed to load power templates:', error);
      toast.error('Erro ao carregar templates de poderes');
      initializePowers({ tank: {}, warrior: {}, support: {} });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (classType, level, field, value) => {
    const setters = {
      tank: setTankPowers,
      warrior: setWarriorPowers,
      support: setSupportPowers
    };
    
    setters[classType](prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [field]: field === 'energy_cost' ? parseInt(value) || 10 : value
      }
    }));
  };

  const handleSavePower = async (classType, level) => {
    const powers = {
      tank: tankPowers,
      warrior: warriorPowers,
      support: supportPowers
    };
    
    const power = powers[classType][level];
    
    if (!power.name?.trim()) {
      toast.error('Nome do poder é obrigatório');
      return;
    }
    
    setSaving(prev => ({ ...prev, [`${classType}-${level}`]: true }));
    
    try {
      await powerAPI.updateTemplate(classType, level, {
        name: power.name,
        description: power.description || '',
        energy_cost: power.energy_cost || 10
      });
      toast.success(`Poder "${power.name}" salvo com sucesso!`);
      loadTemplates();
    } catch (error) {
      console.error('Failed to save power:', error);
      toast.error('Erro ao salvar poder');
    } finally {
      setSaving(prev => ({ ...prev, [`${classType}-${level}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/teacher/dashboard')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="admin-powers-title">
            Configurar Poderes dos Monstros
          </h1>
          <p className="text-slate-600 font-nunito">
            Configure os poderes que cada classe de monstro terá. Os poderes são desbloqueados automaticamente quando o aluno atinge o nível correspondente.
          </p>
        </div>

        {/* Class Tabs */}
        <div className="mb-6">
          <div className="flex gap-2">
            {Object.entries(CLASS_CONFIG).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <Button
                  key={type}
                  data-testid={`${type}-tab`}
                  onClick={() => setSelectedClass(type)}
                  variant={selectedClass === type ? 'default' : 'outline'}
                  className={`flex items-center gap-2 ${selectedClass === type ? '' : `${config.textColor} ${config.borderColor}`}`}
                >
                  <Icon className="w-4 h-4" />
                  {config.name}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Tank Powers */}
        {selectedClass === 'tank' && (
          <PowersGrid
            classType="tank"
            powers={tankPowers}
            templates={templates.tank}
            saving={saving}
            onInputChange={handleInputChange}
            onSave={handleSavePower}
          />
        )}

        {/* Warrior/Atacante Powers */}
        {selectedClass === 'warrior' && (
          <PowersGrid
            classType="warrior"
            powers={warriorPowers}
            templates={templates.warrior}
            saving={saving}
            onInputChange={handleInputChange}
            onSave={handleSavePower}
          />
        )}

        {/* Support Powers */}
        {selectedClass === 'support' && (
          <PowersGrid
            classType="support"
            powers={supportPowers}
            templates={templates.support}
            saving={saving}
            onInputChange={handleInputChange}
            onSave={handleSavePower}
          />
        )}

        {/* Info Card */}
        <Card className="mt-6 p-4 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-800">Como funciona?</h3>
              <p className="text-sm text-amber-700">
                Os poderes configurados aqui serão atribuídos automaticamente aos monstros quando forem criados. 
                Quando o aluno atinge o nível correspondente, o poder é desbloqueado e pode ser usado, 
                consumindo a quantidade de energia especificada.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminPowers;
